import { useCallback, useState } from 'react';
import { Checkbox, Paragraph, Stack, useConfirmationModal } from '@customerio/pluma-components/react';
import { getStorageValue } from '@/hooks/browser/use-local-storage';

// Bumped from -v1 when the modal switched from "show once, then silent" to
// "show every time unless the customer ticks Don't ask me again". Old v1 values
// recorded silent auto-dismiss, which doesn't satisfy the new explicit-opt-out
// contract, so we ignore them.
const AUTO_MODE_CONFIRM_STORAGE_KEY = 'agent-auto-mode-confirmed-v2';

function hasConfirmedAutoMode(): boolean {
  return getStorageValue<boolean>(AUTO_MODE_CONFIRM_STORAGE_KEY, false);
}

function markAutoModeConfirmed(): void {
  try {
    window.localStorage.setItem(AUTO_MODE_CONFIRM_STORAGE_KEY, JSON.stringify(true));
  } catch {
    // localStorage may be unavailable in private mode or with disk full; the next switch
    // will simply re-prompt. Safe failure mode for a confirmation gate.
  }
}

type AutoModeConfirmMessageProps = {
  onDontAskAgainChange: (dontAskAgain: boolean) => void;
};

export function AutoModeConfirmMessage({ onDontAskAgainChange }: AutoModeConfirmMessageProps) {
  const [dontAskAgain, setDontAskAgain] = useState(false);
  return (
    <Stack gap="200">
      <Paragraph>
        Your agent will edit live data without asking you first. You can change this back any time from the input
        toolbar.
      </Paragraph>
      <Checkbox
        label="Don't ask me again"
        isChecked={dontAskAgain}
        onCheckedChange={(checked) => {
          // Normalize to strict boolean per the codebase convention; Pluma's onCheckedChange
          // can emit 'indeterminate', which we don't use here but must not treat as truthy.
          const next = checked === true;
          setDontAskAgain(next);
          onDontAskAgainChange(next);
        }}
      />
    </Stack>
  );
}

/**
 * Shows a confirmation modal before switching the Agent into Auto-approve mode. The modal
 * appears on every switch unless the customer has explicitly checked "Don't ask me again"
 * on a prior switch (state persisted per device in localStorage). Switching from Auto → Ask
 * is always silent and not handled here.
 */
export function useAutoModeConfirmation() {
  const confirm = useConfirmationModal();

  return useCallback(
    (onConfirmed: () => void) => {
      if (hasConfirmedAutoMode()) {
        onConfirmed();
        return;
      }

      // Closure-scoped flag captures the checkbox state across re-renders of the message
      // body. Updated by the body component each time the checkbox toggles; read by the
      // onConfirm callback at click time so we only persist the opt-out when the customer
      // actually ticked the box before confirming.
      let dontAskAgain = false;

      confirm({
        title: 'Auto-approve changes?',
        message: (
          <AutoModeConfirmMessage
            onDontAskAgainChange={(next) => {
              dontAskAgain = next;
            }}
          />
        ),
        confirmLabel: 'Auto-approve',
        onConfirm: () => {
          if (dontAskAgain) {
            markAutoModeConfirmed();
          }
          onConfirmed();
        },
      });
    },
    [confirm],
  );
}
